import { useEffect } from "react";
import toast from "react-hot-toast";

/**
 * Helper to decode Base64 strings containing UTF-8 (Turkish) characters.
 * Standard atob() breaks on multi-byte characters.
 *
 * Also handles URL-decoded Base64 issues (spaces vs +).
 */
const decodeBase64Utf8 = (input: string): string => {
  try {
    // 1. Fix URL decoding artifacts
    // URLSearchParams.get() decodes "+" as a space.
    // Standard Base64 uses "+" for data, so we must convert spaces back to "+".
    const cleanBase64 = input.replace(/ /g, "+");

    // 2. Decode base64 to a "binary string" (where each char is a byte)
    const binaryString = atob(cleanBase64);
    const len = binaryString.length;

    // 3. Convert binary string to an unsigned 8-bit integer array
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    // 4. Use TextDecoder to translate the byte array into a proper UTF-8 string
    return new TextDecoder("utf-8").decode(bytes);
  } catch (error) {
    console.error("[usePaymentError] Decoding failed:", error);
    return "";
  }
};

/**
 * A custom hook to detect and display payment errors passed via URL parameters.
 * Handles UTF-8 (Turkish characters) correctly using TextDecoder.
 */
export const usePaymentError = () => {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const errorParam = params.get("payment_error");

    if (errorParam) {
      // Use the robust decoder
      const decodedError = decodeBase64Utf8(errorParam);

      if (decodedError) {
        // Display the error message
        toast.error(decodedError, {
          duration: 8000, // Error messages stay visible longer
        });

        // Clean up the URL so a refresh doesn't show the error again
        const url = new URL(window.location.href);
        url.searchParams.delete("payment_error");
        window.history.replaceState({}, "", url.toString());
      }
    }
  }, []);
};
